import { useCallback, useEffect, useMemo } from 'react';

import type { DateRange } from '@/components/molecules/DateRangePicker/DateRangePicker';
import {
    DATE_RANGE_PRESETS,
    type DateRangePresetId,
    findMatchingPreset,
} from '@/components/molecules/DateRangePicker/DateRangePresets';

import { useQueryParams } from './useQueryParams';

export interface UseDateRangePickerOptions {
    key?: string;
    defaultPresetId?: DateRangePresetId;
    /**
     * False while the picker is not shown (a page behind a flag): the default range is still
     * returned, but not written into the URL, which should only carry what the page shows.
     * Defaults to true.
     */
    enabled?: boolean;
}

export interface UseDateRangePickerResult {
    dateFrom?: string;
    dateTo?: string;
    selectedPresetId?: DateRangePresetId | null;
    onDateChange: (data: DateRange) => void;
}

/**
 * Custom hook to manage date range state synchronized with URL query parameters.
 * Supports multiple independent date pickers on the same page via custom keys.
 *
 * @param options - Configuration options
 * @param options.key - Optional key to differentiate multiple pickers (e.g., 'tilt', 'weight').
 *                      URL params will be `${key}-from` and `${key}-to`. If not provided, uses 'date-from' and 'date-to'.
 * @param options.defaultPresetId - Default preset to use when no URL params exist
 * @returns Object with dateFrom, dateTo, selectedPresetId, and onDateChange handler
 */
export function useDateRangePicker(
    options: UseDateRangePickerOptions = {}
): UseDateRangePickerResult {
    const { key, defaultPresetId, enabled = true } = options;
    const [queryParams, setQueryParams] = useQueryParams();

    // Build URL param keys using kebab-case
    const fromKey = key ? `${key}-from` : 'date-from';
    const toKey = key ? `${key}-to` : 'date-to';

    // Read dates from URL params or calculate from default preset
    const dateFrom = useMemo(() => {
        const value = queryParams[fromKey];
        if (typeof value === 'string' && value.trim() !== '') {
            return value;
        }
        // If no URL param but we have a default preset, calculate the date
        if (defaultPresetId) {
            const defaultPreset = DATE_RANGE_PRESETS.find((p) => p.id === defaultPresetId);
            if (defaultPreset) {
                const dates = defaultPreset.calculateDates();
                return dates.dateFrom;
            }
        }
        return undefined;
    }, [queryParams, fromKey, defaultPresetId]);

    const dateTo = useMemo(() => {
        const value = queryParams[toKey];
        if (typeof value === 'string' && value.trim() !== '') {
            return value;
        }
        // If no URL param but we have a default preset, calculate the date
        if (defaultPresetId) {
            const defaultPreset = DATE_RANGE_PRESETS.find((p) => p.id === defaultPresetId);
            if (defaultPreset) {
                const dates = defaultPreset.calculateDates();
                return dates.dateTo;
            }
        }
        return undefined;
    }, [queryParams, toKey, defaultPresetId]);

    // Initialize URL params with default preset if they don't exist
    useEffect(() => {
        const hasUrlParams =
            typeof queryParams[fromKey] === 'string' || typeof queryParams[toKey] === 'string';

        if (enabled && !hasUrlParams && defaultPresetId) {
            const defaultPreset = DATE_RANGE_PRESETS.find((p) => p.id === defaultPresetId);
            if (defaultPreset) {
                const dates = defaultPreset.calculateDates();
                // Replace current history entry so browser back returns to the previous page
                setQueryParams(
                    {
                        [fromKey]: dates.dateFrom ?? null,
                        [toKey]: dates.dateTo ?? null,
                    },
                    { replace: true }
                );
            }
        }
    }, [enabled, queryParams, fromKey, toKey, defaultPresetId, setQueryParams]);

    // Detect which preset is currently active based on the current dates
    const selectedPresetId = useMemo(() => {
        return findMatchingPreset(dateFrom, dateTo);
    }, [dateFrom, dateTo]);

    // Handler to update URL params when dates change
    const onDateChange = useCallback(
        (data: DateRange) => {
            setQueryParams({
                [fromKey]: data.dateFrom ?? null,
                [toKey]: data.dateTo ?? null,
            });
        },
        [fromKey, toKey, setQueryParams]
    );

    return {
        dateFrom,
        dateTo,
        selectedPresetId,
        onDateChange,
    };
}
